import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { getJobs } from '../api/jobs';
import dayjs from 'dayjs';

const STATUS_CONFIG = {
    pending:     { label: '대기',    bg: 'warning',   text: 'dark' },
    confirmed:   { label: '예약확정', bg: 'primary',   text: 'white' },
    in_progress: { label: '작업중',  bg: 'info',      text: 'white' },
    completed:   { label: '완료',    bg: 'success',   text: 'white' },
    noshow:      { label: '노쇼',    bg: 'danger',    text: 'white' },
    canceled:    { label: '취소',    bg: 'secondary', text: 'white' },
};

const TodayReservations = ({ onCheckIn, onNewReservation, refreshTrigger }) => {
    const [reservations, setReservations] = useState([]);
    const [completedCount, setCompletedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchTodayReservations = async () => {
            setLoading(true);
            try {
                const todayStart = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
                const todayEnd = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
                const data = await getJobs(todayStart, todayEnd);

                // 완료/노쇼/취소 제외 — 진행할 건만 목록에 노출. 완료는 건수만 별도 집계.
                const visible = data.filter(r => r.status !== 'completed' && r.status !== 'noshow' && r.status !== 'canceled');
                setReservations(visible);
                setCompletedCount(data.filter(r => r.status === 'completed').length);
            } catch (err) {
                console.error("Failed to fetch today's reservations:", err);
                setError('예약 목록을 불러오지 못했습니다.');
            } finally {
                setLoading(false);
            }
        };

        fetchTodayReservations();
    }, [refreshTrigger]);

    const counts = {
        active: reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').length,
        in_progress: reservations.filter(r => r.status === 'in_progress').length,
    };

    return (
        <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
                <strong className="text-primary"><i className="bi bi-calendar-check me-2"></i>오늘의 예약 현황</strong>
                <div className="d-flex gap-2 align-items-center">
                    {!loading && !error && (
                        <>
                            <Badge bg="primary" pill>예약 {counts.active}</Badge>
                            <Badge bg="info" pill>작업중 {counts.in_progress}</Badge>
                            <Badge bg="success" pill>완료 {completedCount}</Badge>
                        </>
                    )}
                    {onNewReservation && (
                        <Button variant="primary" size="sm" className="fw-bold" onClick={onNewReservation}>
                            <i className="bi bi-plus-lg me-1"></i>예약 등록
                        </Button>
                    )}
                </div>
            </Card.Header>
            <ListGroup variant="flush">
                {loading ? (
                    <ListGroup.Item className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                    </ListGroup.Item>
                ) : error ? (
                    <ListGroup.Item className="text-center py-4 text-danger small">{error}</ListGroup.Item>
                ) : reservations.length > 0 ? (
                    reservations.map(res => {
                        const statusCfg = STATUS_CONFIG[res.status] || STATUS_CONFIG.pending;
                        // 확정/대기 → 입고, 작업중 → 완료 처리. 둘 다 모달을 열어 전용 액션 버튼을 쓴다.
                        //   작업중 행에 버튼이 없으면 홈에서 완료로 넘길 방법이 사라진다.
                        const action = (res.status === 'confirmed' || res.status === 'pending')
                            ? { label: '입고 처리', variant: 'outline-primary' }
                            : res.status === 'in_progress'
                                ? { label: '완료 처리', variant: 'outline-success' }
                                : null;
                        return (
                            <ListGroup.Item
                                key={res.id}
                                className="d-flex justify-content-between align-items-center py-3"
                            >
                                <div>
                                    <div className="d-flex align-items-center mb-1 gap-2">
                                        <Badge bg="secondary">{dayjs(res.startTime).format('HH:mm')}</Badge>
                                        <Badge bg={statusCfg.bg} text={statusCfg.text} className="fw-normal">{statusCfg.label}</Badge>
                                        <strong>{res.customerName}</strong>
                                        {res.plateNumber && <span className="text-muted small">({res.plateNumber})</span>}
                                    </div>
                                    <div className="small text-muted">
                                        <span className="me-2">베이 {res.bayNumber}</span>
                                        <span>{res.serviceMenuName || res.serviceName || '메뉴 미지정'}</span>
                                    </div>
                                </div>
                                {action && (
                                    <Button
                                        variant={action.variant}
                                        size="sm"
                                        onClick={() => onCheckIn(res)}
                                    >
                                        {action.label}
                                    </Button>
                                )}
                            </ListGroup.Item>
                        );
                    })
                ) : (
                    <ListGroup.Item className="text-center py-4 text-muted small">
                        오늘 진행할 예약이 없습니다.
                    </ListGroup.Item>
                )}
            </ListGroup>
        </Card>
    );
};

export default TodayReservations;
